<template>
  <kudo-modal
    persist-when-backdrop-clicked
    @close-clicked="newQuoteModal.close"
  >
    <template #header>
      <span class="text-lg">New Client Quote</span>
    </template>
    <div
      v-if="matchedAccount"
      class="flex flex-col gap-y-4"
    >
      <kudo-banner variant="info">
        <template #message>
          <p class="font-medium">
            We found an existing account with the same applicant information. Please visit the account and quote another product from there.
          </p>
        </template>
      </kudo-banner>
      <kudo-card class="border border-solid border-gray-light shadow-none">
        <kudo-block short>
          <p class="font-medium text-sm text-gray-darkest">
            {{ matchedAccount.name }}
          </p>
          <p
            v-if="matchedAccount.number"
            class="text-gray-dark text-xs"
          >
            #{{ matchedAccount.number }}
          </p>
        </kudo-block>
      </kudo-card>
      <div class="font-medium text-sm flex items-center gap-x-1">
        <p>If this is not the same applicant, you can proceed with a</p>
        <button
          type="button"
          class="underline font-semibold"
          :class="{
            'cursor-not-allowed': isBusy,
            'text-gray-dark': isBusy,
            'text-plum-dark': !isBusy,
          }"
          :disabled="isBusy"
          @click="createQuoteAndNavigate"
        >
          new client quote.
        </button>
        <kudo-loader
          :is-loading="isBusy"
          class="ml-2"
        />
      </div>
    </div>
    <kudo-loader
      v-else
      :is-loading="googlePlacesLoading"
      class="min-h-[2rem]"
    >
      <div class="flex flex-col gap-y-6">
        <div
          class="flex items-start gap-4"
          aria-labelledby="step-1 step-address"
        >
          <step-circle
            id="step-1"
            :step="1"
          />
          <div class="flex-1">
            <h2 class="font-medium text-gray-darkest pb-2">
              Address <span class="text-red">*</span>
            </h2>
            <openly-address-autocomplete
              :label="isHomeQuote ? 'Property Address' : 'Address'"
              @google-places-ready="googlePlacesLoading = false"
              @address-updated="(address) => addressFields = address"
              @address-invalidated="addressFields = {}"
            />
          </div>
        </div>
        <div
          v-if="canCreateAutoQuote || isHomeQuote"
          class="flex items-start gap-4"
          aria-labelledby="step-2 step-product"
          >
          <step-circle
            id="step-2"
            :step="2"
          />
          <div class="flex-1 flex flex-col gap-y-2">
            <kudo-form-control
              v-if="canCreateAutoQuote"
              :errors="parentRegle.$fields.productType.$errors"
            >
              <h2 class="font-medium text-gray-darkest">
                Product <span class="text-red">*</span>
              </h2>
              <p class="text-sm font-normal text-gray-darker mb-4">
                Auto is currently only available in WI
              </p>
              <kudo-radio-group
                v-model="parentRegle.$value.productType"
                :radio-options="productOptions"
                aria-label="Product"
                :disabled="$appLock.locked"
                :show-radio-control="false"
                :has-border="true"
                :required="true"
                radio-list-classes="grid sm:grid-cols-3 gap-2 text-center w-full"
                data-cypress="new-quote-product-radios"
                @update:model-value="handleProductTypeChange"
              >
                <template #radios="{ option }">
                  <div class="text-center w-full min-w-0">
                    <p class="font-medium text-gray-darkest text-sm">
                      {{ option.label }}
                    </p>
                  </div>
                </template>
              </kudo-radio-group>
            </kudo-form-control>
            <kudo-expand-transition>
              <div
                v-if="parentRegle.$value.productType === 'home'"
                class="flex flex-col gap-y-3"
              >
                <div class="flex items-center">
                  <h2 class="font-medium">
                    Property Type
                  </h2>
                  <help-button
                    article-id="13644527648535"
                    class="ml-2 text-lg"
                  />
                </div>
                <kudo-form-control>
                  <kudo-radio-group
                    v-model="parentRegle.$value.primaryUse"
                    class="property-type-radio-group"
                    :radio-options="primaryUseOptions"
                    aria-label="Property Type"
                    :show-radio-control="false"
                    :has-border="true"
                    :required="true"
                    radio-list-classes="grid sm:grid-cols-3 gap-2 text-center w-full"
                  >
                    <template #radios="{ option }">
                      <div class="text-center w-full min-w-0">
                        <p class="font-medium text-gray-darkest text-sm">
                          {{ option.label }} <span class="text-gray-dark text-xs">({{ option.subText }})</span>
                        </p>
                      </div>
                    </template>
                  </kudo-radio-group>
                </kudo-form-control>
              </div>
            </kudo-expand-transition>
          </div>
        </div>
        <div
          class="flex items-start gap-4"
          aria-labelledby="step-3 step-applicant"
        >
          <step-circle
            id="step-3"
            :step="3"
          />
          <div
            class="flex-1 flex flex-col gap-y-2"
          >
            <h2 class="font-medium text-gray-darkest pb-2">
              Applicant
            </h2>
            <div class="grid grid-cols-12 gap-y-2 gap-x-4">
              <kudo-form-control
                :errors="parentRegle.$fields.firstName.$errors"
                :helper-text="hasCoApplicant ? 'This is the applicant used for credit pull.' : ''"
                class="col-span-12 md:col-span-6"
              >
                <kudo-input
                  v-model="parentRegle.$value.firstName"
                  :aria-invalid="parentRegle.$fields.firstName.$errors.length"
                  required
                  label-text="First Name"
                  type="text"
                  data-cypress="new-quote-first-name"
                />
              </kudo-form-control>
              <kudo-form-control
                :errors="parentRegle.$fields.middleName.$errors"
                class="col-span-12 md:col-span-6"
              >
                <kudo-input
                  v-model="parentRegle.$value.middleName"
                  :aria-invalid="parentRegle.$fields.middleName.$errors.length"
                  label-text="Middle Name"
                  type="text"
                />
              </kudo-form-control>
              <kudo-form-control
                :errors="parentRegle.$fields.lastName.$errors"
                class="col-span-12 md:col-span-6"
              >
                <kudo-input
                  v-model="parentRegle.$value.lastName"
                  :aria-invalid="parentRegle.$fields.lastName.$errors.length"
                  required
                  label-text="Last Name"
                  type="text"
                  data-cypress="new-quote-last-name"
                />
              </kudo-form-control>
              <kudo-form-control
                class="col-span-12 md:col-span-6"
                :errors="parentRegle.$fields.birthdate.$errors"
              >
                <kudo-input
                  v-model="parentRegle.$value.birthdate"
                  :aria-invalid="parentRegle.$fields.birthdate.$errors.length"
                  placeholder="MM/DD/YYYY"
                  v-bind="$kudoMasks.date"
                  label-text="Date of Birth"
                  required
                  data-cypress="new-quote-dob"
                />
              </kudo-form-control>
              <kudo-expand-transition>
                <div
                  v-if="canCreateAutoQuote && !isHomeQuote && !hasCoApplicant"
                  class="col-span-12 -mt-2"
                >
                  <kudo-button
                    color-variant="secondary"
                    class="w-full mb-6 text-plum-dark border border-gray-light"
                    data-cypress="new-quote-add-coapplicant"
                    @click="addCoApplicant"
                  >
                    <i class="fas fa-plus mr-1" />
                    Add Co-Applicant
                  </kudo-button>
                </div>
              </kudo-expand-transition>
              <kudo-expand-transition>
                <div
                  v-if="hasCoApplicant"
                  class="col-span-12 grid grid-cols-12 gap-y-2 gap-x-4"
                >
                  <kudo-form-control
                    :errors="parentRegle.$fields.coapplicantFirstName.$errors"
                    class="col-span-12 md:col-span-6"
                  >
                    <kudo-input
                      v-model="parentRegle.$value.coapplicantFirstName"
                      :aria-invalid="parentRegle.$fields.coapplicantFirstName.$errors.length"
                      required
                      label-text="Co-Applicant First Name"
                      type="text"
                      data-cypress="new-quote-coapplicant-first-name"
                    />
                  </kudo-form-control>
                  <kudo-form-control
                    :errors="parentRegle.$fields.coapplicantMiddleName.$errors"
                    class="col-span-12 md:col-span-6"
                  >
                    <kudo-input
                      v-model="parentRegle.$value.coapplicantMiddleName"
                      :aria-invalid="parentRegle.$fields.coapplicantMiddleName.$errors.length"
                      label-text="Co-Applicant Middle Name"
                      type="text"
                    />
                  </kudo-form-control>
                  <kudo-form-control
                    :errors="parentRegle.$fields.coapplicantLastName.$errors"
                    class="col-span-12 md:col-span-6"
                  >
                    <kudo-input
                      v-model="parentRegle.$value.coapplicantLastName"
                      :aria-invalid="parentRegle.$fields.coapplicantLastName.$errors.length"
                      required
                      label-text="Co-Applicant Last Name"
                      type="text"
                      data-cypress="new-quote-coapplicant-last-name"
                    />
                  </kudo-form-control>
                  <kudo-form-control
                    class="col-span-12 md:col-span-6"
                    :errors="parentRegle.$fields.coapplicantBirthdate.$errors"
                  >
                    <kudo-input
                      v-model="parentRegle.$value.coapplicantBirthdate"
                      :aria-invalid="parentRegle.$fields.coapplicantBirthdate.$errors.length"
                      placeholder="MM/DD/YYYY"
                      v-bind="$kudoMasks.date"
                      label-text="Co-Applicant Date of Birth"
                      required
                      data-cypress="new-quote-coapplicant-dob"
                    />
                  </kudo-form-control>
                  <div class="col-span-12 -mt-3 flex items-center justify-between gap-4">
                    <kudo-checkbox
                      v-model="parentRegle.$value.isSpouse"
                      data-cypress="new-quote-coapplicant-is-spouse"
                    >
                      <span class="text-sm">Co-applicant is the spouse of the applicant</span>
                    </kudo-checkbox>
                    <kudo-button
                      shape-variant="thin"
                      color-variant="plain"
                      class="w-fit py-4 text-plum-dark font-semibold"
                      @click="removeCoApplicant"
                    >
                      <i class="fas fa-minus mr-1" />
                      Remove Co-Applicant
                    </kudo-button>
                  </div>
                </div>
              </kudo-expand-transition>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-y-2 text-xs text-gray-darker mt-3 ml-12">
        <p>
          <span class="font-semibold">IMPORTANT REQUIRED READING:</span>
          Before proceeding, please ensure the applicable
          <a
            class="text-plum-dark underline font-semibold"
            target="_blank"
            href="https://help.openly.com/hc/en-us/articles/23837245678231-What-are-Openly-s-State-Disclosures"
          >
            state disclosure
          </a>
          has been read to the applicant.
          <span class="font-semibold">By continuing, you acknowledge that this required disclosure has been provided.</span>
        </p>
      </div>
    </kudo-loader>
    <template #footer>
      <div
        v-if="matchedAccount"
        class="flex gap-x-4 justify-end"
      >
        <kudo-button
          color-variant="secondary"
          :is-disabled="isBusy"
          @click="newQuoteModal.close"
        >
          Cancel
        </kudo-button>
        <kudo-button
          :is-disabled="isBusy"
          data-cypress="new-quote-match-found-go-to-account-button"
          @click="goToMatchedAccount"
        >
          Quote from Account
        </kudo-button>
      </div>
      <div
        v-else
        class="flex gap-x-4 justify-end"
      >
        <kudo-button
          color-variant="secondary"
          @click="newQuoteModal.close"
        >
          Cancel
        </kudo-button>
        <kudo-button
          :is-disabled="isBusy"
          data-cypress="new-quote-create-quote-button"
          @click="submitQuote"
        >
          <kudo-loader :is-loading="isBusy">
            Create Quote
            <i class="fas fa-arrow-right ml-1" />
          </kudo-loader>
        </kudo-button>
      </div>
    </template>
  </kudo-modal>
</template>

<script setup>
import { useCollectScope, useScopedRegle } from '@regle/core'
import dayjs from 'dayjs'
import { required, requiredIf, maxLength, withMessage, dateAfter } from '@regle/rules'
import { KudoModal, KudoInput, KudoFormControl, KudoButton, KudoLoader, KudoRadioGroup, KudoExpandTransition, KudoBanner, KudoCard, KudoBlock, KudoCheckbox } from '@openly-engineering/kudo'
import { useMutation, useQuery } from '@urql/vue'
import { CREATE_HOME_QUOTE, CREATE_AUTO_QUOTE } from './mutations'
import { MATCH_ACCOUNT_BY_APPLICANTS } from './queries'

const { hook, $featureFlags, $appLock, $store } = useNuxtApp()
const newQuoteModal = inject('newQuoteModal')
const awaitingNavigation = ref(false)
const googlePlacesLoading = ref(true)
const matchedAccount = ref(null)

defineExpose({ awaitingNavigation })

const createQuote = useMutation(CREATE_HOME_QUOTE)
const createAutoQuote = useMutation(CREATE_AUTO_QUOTE)
const autoQuoteCapability = computed(() => $store.state.self.capabilities?.canCreateAutoQuote)
const canCreateAutoQuote = computed(() => $featureFlags.useFlag('ui-auto-quoting-enabled').value && autoQuoteCapability.value?.answer === true)
const isAccountsEnabled = computed(() => $featureFlags.useFlag('ui-accounts-enabled').value)
const isHomeQuote = computed(() => parentRegle.$value.productType === 'home')
// Auto quoting is only available in states that are both supported by Openly and enabled for the agent.
const isAutoQuoteEnabled = computed(() =>
  Boolean(addressFields.value?.state) &&
  autoQuoteCapability.value?.states?.includes(addressFields.value.state)
)
const hasCoApplicant = ref(false)
const isBusy = computed(
  () => createQuote.fetching.value || createAutoQuote.fetching.value || awaitingNavigation.value || matchAccountQuery.fetching.value
)

const productOptions = computed(() => [
  { label: 'Home', value: 'home' },
  { label: 'Auto', value: 'auto', disabled: !isAutoQuoteEnabled.value },
])

const primaryUseOptions = [
  { label: 'Main Residence', value: 'Main', subText: 'HO-5' },
  { label: 'Secondary/Seasonal', value: 'Secondary', subText: 'HO-5' },
  { label: 'Long Term Rental', value: 'Landlord', subText: 'HO-3' },
]

const addressFields = ref({})

const formFields = ref({
  productType: 'home',
  firstName: '',
  middleName: '',
  lastName: '',
  birthdate: '',
  primaryUse: 'Main',
  coapplicantFirstName: '',
  coapplicantMiddleName: '',
  coapplicantLastName: '',
  coapplicantBirthdate: '',
  isSpouse: true,
})

const { r$: parentRegle } = useScopedRegle(formFields, {
  firstName: { required, maxLength: maxLength(20) },
  middleName: { maxLength: maxLength(20) },
  lastName: { required, maxLength: maxLength(30) },
  birthdate: {
    required,
    // Match BE validation date
    dateAfter: withMessage(dateAfter('1900-01-01'), 'Birthdate must be no earlier than 01/01/1900'),
    // Auto only: applicant must be 21, counting anyone within 30 days of turning 21.
    // Check their age as of 30 days from now — if they're 21 by then, they qualify (grace window).
    minApplicantAge: withMessage((value) => {
      if (formFields.value.productType !== 'auto') { return true }
      const birth = dayjs(value)
      if (!value || value.length < 10) { return true }
      return dayjs().add(30, 'day').diff(birth, 'year') >= 21
    }, 'Applicant age cannot be less than 21.'),
  },
  coapplicantFirstName: { required: requiredIf(() => hasCoApplicant), maxLength: maxLength(20) },
  coapplicantMiddleName: { maxLength: maxLength(20) },
  coapplicantLastName: { required: requiredIf(() => hasCoApplicant), maxLength: maxLength(30) },
  coapplicantBirthdate: {
    required: requiredIf(() => hasCoApplicant),
    // Match BE validation date
    dateAfter: withMessage(dateAfter('1900-01-01'), 'Birthdate must be no earlier than 01/01/1900'),
  },
  productType: { required },
})

const { r$ } = useCollectScope()

const applicantsToMatch = computed(() => {
  const applicants = [{
    firstName: parentRegle.$value.firstName,
    middleName: parentRegle.$value.middleName,
    lastName: parentRegle.$value.lastName,
    birthdate: dayjs(parentRegle.$value.birthdate).format('YYYY-MM-DD'),
  }]

  if (hasCoApplicant.value) {
    applicants.push({
      firstName: parentRegle.$value.coapplicantFirstName,
      middleName: parentRegle.$value.coapplicantMiddleName,
      lastName: parentRegle.$value.coapplicantLastName,
      birthdate: dayjs(parentRegle.$value.coapplicantBirthdate).format('YYYY-MM-DD'),
    })
  }

  return applicants
})

const matchAccountQuery = useQuery({
  query: MATCH_ACCOUNT_BY_APPLICANTS,
  variables: computed(() => ({ input: { applicants: applicantsToMatch.value } })),
  requestPolicy: 'network-only',
  pause: true,
})

let pageFinishUnsubscribe

// This ensures the `page:finish` hook subscription is unsubscribed from regardless of why the component was unmounted.
onUnmounted(() => pageFinishUnsubscribe?.())

function handleProductTypeChange(value) {
  if (value === 'home') {
    removeCoApplicant()
  }
}

watch(isAutoQuoteEnabled, (eligible) => {
  if (!eligible && parentRegle.$value.productType === 'auto') {
    parentRegle.$value.productType = 'home'
    handleProductTypeChange('home')
  }
})

function addCoApplicant() {
  hasCoApplicant.value = true
}

function removeCoApplicant() {
  hasCoApplicant.value = false
  parentRegle.$value.coapplicantFirstName = ''
  parentRegle.$value.coapplicantMiddleName = ''
  parentRegle.$value.coapplicantLastName = ''
  parentRegle.$value.coapplicantBirthdate = ''
  parentRegle.$value.isSpouse = true
}

function getQuotePayload(type) {
  let payload
  let mutationToExecute

  if (type === 'home') {
    mutationToExecute = createQuote
    payload = {
      firstName: parentRegle.$value.firstName,
      middleName: parentRegle.$value.middleName,
      lastName: parentRegle.$value.lastName,
      birthdate: dayjs(parentRegle.$value.birthdate).format('YYYY-MM-DD'),
      primaryUse: parentRegle.$value.primaryUse,
      streetNumber: addressFields.value.streetNumber,
      streetName: addressFields.value.streetName,
      city: addressFields.value.city,
      zip: addressFields.value.zip,
      state: addressFields.value.state,
    }
  } else {
    mutationToExecute = createAutoQuote
    payload = {
      effectiveDate: dayjs().add(9, 'days').format('YYYY-MM-DD'),
      location: {
        streetNumber: addressFields.value.streetNumber,
        streetName: addressFields.value.streetName,
        city: addressFields.value.city,
        zip: addressFields.value.zip,
        state: addressFields.value.state,
      },
      applicant: {
        birthdate: dayjs(parentRegle.$value.birthdate).format('YYYY-MM-DD'),
        firstName: parentRegle.$value.firstName,
        middleName: parentRegle.$value.middleName,
        lastName: parentRegle.$value.lastName,
      },
    }
    if (hasCoApplicant.value) {
      payload.coapplicant = {
        birthdate: dayjs(parentRegle.$value.coapplicantBirthdate).format('YYYY-MM-DD'),
        firstName: parentRegle.$value.coapplicantFirstName,
        middleName: parentRegle.$value.coapplicantMiddleName,
        lastName: parentRegle.$value.coapplicantLastName,
        isSpouse: parentRegle.$value.isSpouse,
      }
    }
  }
  return { payload, mutationToExecute }
}

async function submitQuote() {
  // we must first await the validation due to the $silent nature of the address autocomplete component's regle scope
  await r$.$validate()

  if (r$.$invalid) {
    r$.$touch()
    return
  }

  if (isAccountsEnabled.value) {
    const { data, error } = await matchAccountQuery.executeQuery()

    // On error, fall through and create the quote rather than blocking the agent.
    const account = error.value ? null : data.value?.matchAccountByApplicants

    if (account) {
      matchedAccount.value = { id: account.id, name: account.name, number: account.number }
      return
    }
  }

  await createQuoteAndNavigate()
}

async function goToMatchedAccount() {
  const accountID = matchedAccount.value?.id
  const targetRoute = `/account/${accountID}`

  // If we're already on the matched account's page, navigateTo is a no-op and the
  // page:finish hook never fires, which would leave the modal hanging on awaitingNavigation.
  // Just close the modal directly in that case.
  if (useRoute().path === targetRoute) {
    newQuoteModal.close()
    return
  }

  awaitingNavigation.value = true
  await navigateTo(targetRoute)
  pageFinishUnsubscribe = hook('page:finish', () => {
    awaitingNavigation.value = false
    newQuoteModal.close()

    pageFinishUnsubscribe()
    pageFinishUnsubscribe = null
  })
}

async function createQuoteAndNavigate() {
  awaitingNavigation.value = true
  const { mutationToExecute, payload } = getQuotePayload(parentRegle.$value.productType)

  const { error, data } = await $appLock.lockUntil(mutationToExecute.executeMutation({
    data: payload,
  }))

  if (error) {
    awaitingNavigation.value = false
    return
  }

  if (parentRegle.$value.productType === 'home') {
    const policyNumber = data.createQuote?.number
    await navigateTo(`/home/${policyNumber}/1/1/change/property`)

    window.analytics.track('Home Quote Created', {
      policy: policyNumber,
      transaction: data.createQuote?.transaction,
    })
  } else {
    const policyNumber = data.createAutoQuote?.policy?.policyNumber
    const quoteNumber = data.createAutoQuote?.id
    await navigateTo(`/auto/${policyNumber}/change/${quoteNumber}/household`)

    window.analytics.track('Auto Quote Created', {
      quote: quoteNumber,
      policy: policyNumber,
    })
  }

  // Use Nuxt's navigation hook to close the modal only after the new page has finished loading.
  // The awaitingNavigation flag prevents duplicate submissions while waiting for navigation to complete.
  // This improves UX by ensuring the modal closes after the route and its data are fully loaded, avoiding suspense-related delays.
  pageFinishUnsubscribe = hook('page:finish', () => {
    awaitingNavigation.value = false
    newQuoteModal.close()

    pageFinishUnsubscribe()
    pageFinishUnsubscribe = null
  })
}
</script>
